import { Modal } from "antd";
import { useQuery } from "@tanstack/react-query";
import { Form, Formik, FormikValues } from "formik";
import { useState } from "react";
import * as Yup from "yup";

import { Button, Input, Select } from "../../custom";
import api from "../../utils/api";
import useChangeCourseMutation from "../../hooks/api/useChangeCourseMutation";
import useGetSpecializationByProgramIdQuery from "../../hooks/api/useGetSpecializationByProgramIdQuery";
import styles from "./styles.module.scss";

interface ChangeCourseModalProps {
  applicantId?: string;
  open: boolean;
  onClose: () => void;
}

interface ChangeCourseFormValues {
  newProgramId: string;
  newConcentrationId: string;
  reason: string;
}

interface ProgramDetailsResponse {
  statusCode: number;
  message: string;
  data: ProgramDetails[];
}

interface ProgramDetails {
  id: number;
  programTypeId: number;
  programId: number;
  categoryCode: string;
  categoryCodeName: string | null;
  programTypeName: string;
  program: string;
  activeStatus: boolean;
  isDeleted: boolean;
  programName: string;
  programCode?: string;
  institutionShortName?: string;
}

const changeCourseValidationSchema = Yup.object().shape({
  newProgramId: Yup.string().required("New program is required"),
  reason: Yup.string().trim().required("Reason is required"),
});

const getAllProgramDetails = async () => {
  return (await api.get("/Utilities/Utilities/GetAllProgramDetails"))
    .data as ProgramDetailsResponse;
};

const ChangeCourseModal = ({
  applicantId,
  open,
  onClose,
}: ChangeCourseModalProps) => {
  const [selectedProgramId, setSelectedProgramId] = useState(0);
  const changeCourseMutation = useChangeCourseMutation();

  const programQuery = useQuery({
    queryKey: ["get-program-details"],
    queryFn: getAllProgramDetails,
  });

  const specializationQuery = useGetSpecializationByProgramIdQuery({
    Id: selectedProgramId,
  });

  const programs =
    programQuery.data?.data?.filter((program) => program.activeStatus) ?? [];

  const programOptions = programs.map((program) => (
    <option key={program.id} value={program.id}>
      {program.programName}
    </option>
  ));

  const concentrationOptions = specializationQuery.specializations?.map(
    (concentration) => (
      <option key={concentration.id} value={concentration.id}>
        {concentration.name}
      </option>
    ),
  );

  const handleClose = () => {
    setSelectedProgramId(0);
    onClose();
  };

  const handleSubmit = async (values: FormikValues) => {
    await changeCourseMutation.changeCourseHandler({
      applicantId: applicantId ?? "",
      newProgramId: Number(values.newProgramId),
      newConcentrationId: Number(values.newConcentrationId) || 0,
      reason: values.reason,
    });

    handleClose();
  };

  return (
    <Modal
      open={open}
      title="Change Course"
      footer={null}
      onCancel={handleClose}
      destroyOnClose
    >
      <Formik<ChangeCourseFormValues>
        initialValues={{
          newProgramId: "",
          newConcentrationId: "",
          reason: "",
        }}
        validationSchema={changeCourseValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ handleChange, setFieldValue }) => (
          <Form className={styles.changeCourseForm}>
            <Select
              name="newProgramId"
              label="New Program"
              placeholder={
                programQuery.isLoading ? "Loading programs" : "Select program"
              }
              options={programOptions}
              disabled={programQuery.isLoading}
              onChange={(event) => {
                handleChange(event);
                const programId = Number(event.target.value);
                setSelectedProgramId(programId);
                setFieldValue("newConcentrationId", "");
              }}
            />
            <Select
              name="newConcentrationId"
              label="New Concentration"
              placeholder={
                specializationQuery.isLoading
                  ? "Loading concentrations"
                  : "Select concentration"
              }
              options={concentrationOptions}
              disabled={!selectedProgramId || specializationQuery.isLoading}
            />
            <Input
              type="textarea"
              name="reason"
              label="Reason"
              placeholder="Enter reason for changing course"
            />
            <div className={styles.changeCourseButtons}>
              <Button type="button" text="Cancel" onClick={handleClose} />
              <Button
                type="submit"
                text="Submit"
                isLoading={changeCourseMutation.isLoading}
                disabled={changeCourseMutation.isLoading}
              />
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ChangeCourseModal;
